import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  creditParameters,
  type CreditInheritance,
  type CreditInheritanceMode,
  type CreditRuleSpec,
} from '@/shared/api/resources';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { SkeletonText } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/useToast';

import { mensagemDoErro, rotaAusente } from './escopo';
import {
  rotuloDaClassificacao,
  textoDaDisponibilidade,
  tomDaClassificacao,
} from './valores';

/**
 * Regras do motor e as pendencias de cada uma: a classificacao da fonte, os
 * conflitos abertos, o que a regra consegue decidir hoje e se esta homologada na
 * versao ativa. E para onde a tela leva quem abre um grupo sem parametro.
 */
export function PainelDeRegras({
  regras,
  homologadasNaAtiva,
  temAtiva,
}: {
  regras: CreditRuleSpec[];
  homologadasNaAtiva: string[];
  temAtiva: boolean;
}) {
  return (
    <Card>
      <CardHeader
        title="Regras e pendencias"
        description="As regras do motor, com a classificacao da fonte e os conflitos ainda abertos."
      />
      <CardBody className="flex flex-col gap-4">
        <Alert tone="info" title="Onde estao as pendencias">
          As regras CONFLITANTE e PENDENTE e os conflitos citados (CONF, INC, PEN) estao
          registrados em docs/OPEN_QUESTIONS.md e na especificacao 41. Um grupo sem
          parametro e um grupo sem regra documentada que possa ser parametrizada: o
          valor so entra quando a fonte for aprovada.
        </Alert>
        {!temAtiva ? (
          <Alert tone="warning" title="Nenhuma versao ativa">
            Sem versao ativa, toda regra roda em sombra e nada decide (PM-01).
          </Alert>
        ) : null}
        <DataTable<CreditRuleSpec>
          columns={[
            {
              key: 'regra',
              header: 'Regra',
              render: (regra) => (
                <div className="flex flex-col">
                  <span className="font-mono text-2xs text-ink-500">{regra.id}</span>
                  <span className="font-medium text-ink-900">{regra.name}</span>
                </div>
              ),
            },
            {
              key: 'classificacao',
              header: 'Classificacao',
              render: (regra) => (
                <Badge tone={tomDaClassificacao(regra.classification)}>
                  {rotuloDaClassificacao(regra.classification)}
                </Badge>
              ),
            },
            {
              key: 'conflitos',
              header: 'Pendencias',
              render: (regra) => (regra.conflicts ?? []).join(', ') || '-',
            },
            {
              key: 'hoje',
              header: 'O que decide hoje',
              render: (regra) => textoDaDisponibilidade(regra.availability),
            },
            {
              key: 'homologada',
              header: 'Homologada na ativa',
              render: (regra) =>
                !regra.evaluated
                  ? 'Sem avaliacao propria'
                  : homologadasNaAtiva.includes(regra.id)
                    ? 'Sim'
                    : 'Nao (sombra)',
            },
            {
              key: 'premissa',
              header: 'Premissa',
              render: (regra) => (
                <span className="text-xs text-ink-600">{regra.premise || '-'}</span>
              ),
            },
          ]}
          items={regras}
          keyOf={(regra) => regra.id}
        />
      </CardBody>
    </Card>
  );
}

/**
 * Heranca do padrao global: a empresa fica com os parametros proprios ou herda o
 * padrao global. Herdar cria rascunho a cada versao global nova, e nunca ativa.
 */
export function PainelDeHeranca({ podeEditar }: { podeEditar: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const consulta = useQuery({
    queryKey: ['credito-parametros', 'heranca'],
    queryFn: ({ signal }) => creditParameters.inheritance(signal),
    retry: false,
  });
  const [escolha, setEscolha] = useState<CreditInheritanceMode | null>(null);
  const atual = consulta.data?.mode ?? 'propria';
  const modo = escolha ?? atual;

  const salvar = useMutation({
    mutationFn: (novo: CreditInheritanceMode) => creditParameters.setInheritance(novo),
    onSuccess: (resposta) => {
      queryClient.setQueryData(['credito-parametros', 'heranca'], resposta);
      void queryClient.invalidateQueries({
        queryKey: ['credito-parametros', 'versoes'],
      });
      setEscolha(null);
      toast.success(
        resposta.mode === 'herda_padrao_global'
          ? 'A empresa passa a herdar o padrao global.'
          : 'A empresa passa a usar parametros proprios.',
      );
    },
  });

  return (
    <Card>
      <CardHeader
        title="Heranca do padrao global"
        description="Se a empresa segue os parametros que a administracao da plataforma publica."
      />
      <CardBody className="flex flex-col gap-4">
        {consulta.isPending ? <SkeletonText lines={3} /> : null}
        {consulta.isError ? (
          rotaAusente(consulta.error) ? (
            <Alert tone="info" title="Heranca ainda nao disponivel">
              Esta instalacao ainda nao tem o padrao global da plataforma. A empresa usa
              os parametros proprios, editados e ativados nesta tela.
            </Alert>
          ) : (
            <Alert tone="danger" title="Nao foi possivel ler a heranca">
              {mensagemDoErro(consulta.error)}
            </Alert>
          )
        ) : null}
        {consulta.isSuccess ? (
          <>
            <Alert tone="info" title="Herdar nunca ativa sozinho">
              Com a heranca, cada versao nova do padrao global abre um rascunho nesta
              empresa. O rascunho nao entra em vigor sozinho: a ativacao continua sendo
              feita aqui, com justificativa, por quem tem a permissao de aprovar e nao
              editou a versao.
            </Alert>
            <fieldset
              disabled={!podeEditar || salvar.isPending}
              className="flex flex-col gap-2"
            >
              <legend className="mb-1 text-xs font-semibold text-ink-700">
                Parametros da empresa
              </legend>
              <label className="flex items-center gap-2 text-sm text-ink-800">
                <input
                  type="radio"
                  name="heranca"
                  checked={modo === 'propria'}
                  onChange={() => setEscolha('propria')}
                />
                Parametros proprios
              </label>
              <label className="flex items-center gap-2 text-sm text-ink-800">
                <input
                  type="radio"
                  name="heranca"
                  checked={modo === 'herda_padrao_global'}
                  onChange={() => setEscolha('herda_padrao_global')}
                />
                Herdar do padrao global
              </label>
            </fieldset>
            {consulta.data.updated_at ? (
              <p className="text-2xs text-ink-500">
                {`Ultima mudanca por ${consulta.data.updated_label || 'sistema'} em ${formatDateTime(consulta.data.updated_at)}.`}
              </p>
            ) : null}
            <SituacaoDoPadraoGlobal heranca={consulta.data} />
            {salvar.isError ? (
              <Alert tone="danger" title="Nao foi possivel salvar">
                {mensagemDoErro(salvar.error)}
              </Alert>
            ) : null}
            {podeEditar ? (
              <div className="flex justify-end">
                <Button
                  disabled={modo === atual}
                  loading={salvar.isPending}
                  onClick={() => salvar.mutate(modo)}
                >
                  Salvar heranca
                </Button>
              </div>
            ) : (
              <p className="text-xs text-ink-500">
                O seu perfil nao inclui a alteracao dos parametros de credito.
              </p>
            )}
          </>
        ) : null}
      </CardBody>
    </Card>
  );
}

/**
 * O padrao global em vigor, a ultima versao global que a empresa recebeu e os avisos
 * da heranca, como a versao global que espera o rascunho aberto da empresa.
 */
function SituacaoDoPadraoGlobal({ heranca }: { heranca: CreditInheritance }) {
  const global = heranca.global_default;
  const avisos = heranca.notices ?? [];
  return (
    <div className="flex flex-col gap-2 text-sm text-ink-700">
      <p>
        {global
          ? `Padrao global em vigor: versao ${global.version}${
              global.effective_from
                ? `, desde ${formatDateTime(global.effective_from)}`
                : ''
            }.`
          : 'Nenhuma versao do padrao global esta em vigor.'}
      </p>
      {heranca.received_global_version ? (
        <p>{`A empresa ja recebeu valores da versao ${heranca.received_global_version} do padrao global.`}</p>
      ) : null}
      {heranca.pending_global_version ? (
        <Alert tone="warning" title="Versao global a espera do rascunho aberto">
          {`A versao ${heranca.pending_global_version} do padrao global ainda nao virou rascunho nesta empresa porque ha outro rascunho aberto. Ative ou descarte o rascunho para receber os valores globais.`}
        </Alert>
      ) : null}
      {avisos.map((aviso) => (
        <Alert
          key={`${aviso.global_version}:${aviso.draft_version}:${aviso.kind}`}
          tone="info"
          title={`Versao ${aviso.global_version} do padrao global`}
        >
          {aviso.message}
        </Alert>
      ))}
    </div>
  );
}
